import moment from 'moment'

/**
 * "Presa visione" (patient acknowledgement) of a COMPLETED activity.
 * Persisted by psp-backend on the activity instance as `patientAcknowledgement`
 * (POST /user-events/activities/:activityId/ack).
 */
export interface PatientAcknowledgement {
  isConfirmed: boolean
  confirmedAt?: string
  /** Who confirmed: the beneficiary today, possibly a caregiver later. Missing on older acknowledgements. */
  confirmedBy?: {userId?: string; firstName?: string; lastName?: string; userGroup?: string}
  signature?: {_id?: string; file: string; name?: string; size?: number}
}

const ACKNOWLEDGEABLE_STATE = 'COMPLETED'

/**
 * Only real activity instances (they carry a `typeCode`, unlike the
 * PERSONAL_EVENT/VIRTUAL_MEETING appointments returned by /user-events/)
 * that are COMPLETED, not internal (a missing `isInternal`, e.g. on-demand
 * activities, counts as not internal), and whose beneficiary is the logged user.
 */
export function isAcknowledgeableActivity(event: Record<string, any> | undefined, loggedUserId: string | undefined): boolean {
  if (!event || !loggedUserId) return false
  return Boolean(event.typeCode)
    && event.state === ACKNOWLEDGEABLE_STATE
    && event.isInternal !== true
    && event.beneficiaryId === loggedUserId
}

export function isActivityAcknowledged(event: Record<string, any> | undefined): boolean {
  return Boolean(event?.patientAcknowledgement?.isConfirmed)
}

export function getActivityAckTooltip(event: Record<string, any> | undefined): string {
  if (!isActivityAcknowledged(event)) return 'Presa visione da confermare'
  const {confirmedAt, confirmedBy} = event?.patientAcknowledgement ?? {}
  const date = confirmedAt ? ` il ${moment(confirmedAt).format('DD/MM/YYYY HH:mm')}` : ''
  const fullName = [confirmedBy?.firstName, confirmedBy?.lastName].filter(Boolean).join(' ')
  const author = fullName ? ` da ${fullName}` : ''
  return `Presa visione confermata${date}${author}`
}

/** Converts the signature pad's PNG data URL into a File to upload as multipart. */
export function signatureDataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64] = dataUrl.split(',')
  const mimeType = header.split(':')[1].split(';')[0]
  const byteString = atob(base64)
  const bytes = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i)
  }
  return new File([bytes], fileName, {type: mimeType})
}
